import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';
import useAutoRefresh, { UpdatedAgo } from './useAutoRefresh';
import { relativeSeen, utilisationTone, DEVICE_TONE } from './format';

const prettyDate = (iso) => {
    if (!iso) return '';
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
        timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
};

/**
 * My own working time today.
 *
 * Devices are read-only here — no revoke, no delete. Revoking is an
 * administrative act, and a user switching off their own tracking is exactly
 * what this module exists to prevent.
 */
export default function MyToday() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [punch, setPunch] = useState(null);
    const [punchBusy, setPunchBusy] = useState(false);
    const [punchError, setPunchError] = useState('');

    // `silent` skips the loading state, so a background poll does not put the
    // Refresh button into "Refreshing…" once a minute on its own.
    const load = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        setError('');
        return axiosInstance.get('/api/working-time/me/today')
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.message || 'Could not load your working time'))
            .finally(() => setLoading(false));
    }, []);

    const loadPunch = useCallback(() => (
        axiosInstance.get('/api/working-time/me/attendance/today')
            .then((r) => setPunch(r.data))
            .catch(() => { /* punch strip just stays hidden on failure */ })
    ), []);

    useEffect(() => { load(); loadPunch(); }, [load, loadPunch]);

    const doPunch = useCallback((direction) => {
        setPunchBusy(true);
        setPunchError('');
        axiosInstance.post(`/api/working-time/me/attendance/${direction}`)
            .then(() => Promise.all([loadPunch(), load(true)]))
            .catch((e) => setPunchError(e.response?.data?.message || 'Could not record that.'))
            .finally(() => setPunchBusy(false));
    }, [loadPunch, load]);

    // This tab is always today, so it always has figures that can still move.
    const lastUpdated = useAutoRefresh(load);

    if (loading && !data) return <div className="wt-empty">Loading…</div>;
    if (error && !data) return <div className="wt-error">{error}</div>;
    if (!data) return null;

    const row = data.row;
    const idleMinutes = Math.round(((data.settings && data.settings.idleThresholdSeconds) || 300) / 60);

    return (
        <div>
            {error && <div className="wt-error">{error}</div>}

            <div className="wt-hero">
                <div className="wt-hero-head">
                    <div>
                        <h2 className="wt-hero-title">{data.employee.name}</h2>
                        <div className="wt-hero-date">{prettyDate(data.date)}</div>
                    </div>
                    <div className="wt-hero-actions">
                        <UpdatedAgo at={lastUpdated} />
                        <button type="button" className="wt-btn" onClick={() => load()} disabled={loading}>
                            {loading ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {row ? (
                    <div className="wt-hero-figures">
                        <div className="wt-figure">
                            <div className="k">Active</div>
                            <div className="v accent">{row.activeHours}</div>
                        </div>
                        <div className="wt-figure">
                            <div className="k">Idle</div>
                            <div className="v">{row.idleHours}</div>
                        </div>
                        <div className="wt-figure">
                            <div
                                className="k"
                                title="Time the agent was locked/off or otherwise not actively working."
                            >
                                Away
                            </div>
                            <div className="v">{row.awayHours}</div>
                        </div>
                        <div className="wt-figure">
                            <div className="k">Total</div>
                            <div className="v">{row.totalHours}</div>
                        </div>
                        <div className="wt-figure">
                            <div className="k">Active share</div>
                            <div className="v">
                                <span className={`wt-badge ${utilisationTone(row.utilisation)}`}>
                                    {row.utilisation}%
                                </span>
                            </div>
                        </div>
                        <div className="wt-figure">
                            <div className="k">First active</div>
                            <div className="v">{row.firstActiveTime || '-'}</div>
                        </div>
                        <div className="wt-figure">
                            <div className="k">Last active</div>
                            <div className="v">{row.lastActiveTime || '-'}</div>
                        </div>
                    </div>
                ) : (
                    <div className="wt-empty">
                        Nothing recorded yet today. Figures appear once the agent on your laptop
                        sends its next report.
                    </div>
                )}

                <div className="wt-device-row">
                    {data.devices.length === 0 && (
                        <span className="wt-hero-date">
                            No laptop is enrolled against your account yet.
                        </span>
                    )}
                    {data.devices.map((d) => (
                        <span key={d._id} className="wt-device-chip">
                            <span className={`wt-badge ${DEVICE_TONE[d.status] || 'muted'}`}>
                                {d.statusLabel}
                            </span>
                            <span className="name">{d.deviceName}</span>
                            <span className="seen">{relativeSeen(d.minutesSinceSeen)}</span>
                        </span>
                    ))}
                </div>
            </div>

            <div className="wt-hero wt-punch">
                <div className="wt-hero-head">
                    <div>
                        <h2 className="wt-hero-title">Check in / Check out</h2>
                        <div className="wt-hero-date">
                            {punch && punch.open
                                ? `Checked in at ${punch.open.checkInTime}`
                                : 'Not checked in'}
                        </div>
                    </div>
                    <div className="wt-hero-actions">
                        {punch && punch.open ? (
                            <button
                                type="button"
                                className="wt-btn primary"
                                onClick={() => doPunch('check-out')}
                                disabled={punchBusy}
                            >
                                {punchBusy ? 'Working…' : 'Check out'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="wt-btn primary"
                                onClick={() => doPunch('check-in')}
                                disabled={punchBusy}
                            >
                                {punchBusy ? 'Working…' : 'Check in'}
                            </button>
                        )}
                    </div>
                </div>

                {punchError && <div className="wt-error">{punchError}</div>}

                {punch && punch.punches.length > 0 ? (
                    <div className="wt-punch-scroll">
                    <table className="wt-punch-table">
                        <thead>
                            <tr>
                                <th>In</th>
                                <th>Out</th>
                                <th>Elapsed</th>
                                <th>Active</th>
                                <th>Idle</th>
                                <th title="Screen locked, signed out, laptop asleep or off, or agent not running.">Away (locked / off)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {punch.punches.map((p) => (
                                <tr key={p._id}>
                                    <td>{p.checkInTime}</td>
                                    <td>{p.checkOutTime || <span className="wt-badge good">open</span>}</td>
                                    <td>{p.elapsedHours || '—'}</td>
                                    <td>{p.windowed ? p.windowed.activeHours : '—'}</td>
                                    <td>{p.windowed ? p.windowed.idleHours : '—'}</td>
                                    <td>
                                        {p.windowed ? p.windowed.awayHours : '—'}
                                        {p.windowed && !p.windowed.sampled && (
                                            <span
                                                className="wt-badge"
                                                title="Part of this day was uploaded late (laptop offline), so this split is approximate."
                                            >
                                                approx.
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                ) : (
                    <div className="wt-empty">No check-in recorded today.</div>
                )}
            </div>

            <p className="wt-note">
                Active time is measured from keyboard and mouse activity only. After{' '}
                <strong>{idleMinutes}</strong> minutes without input the agent marks you idle and
                stops counting active time. Away is screen-locked, signed-out, sleeping or
                shut-down time — shown so the gap between Total and your day is never a mystery, but
                it is not counted toward it. Total = active + idle.
            </p>
        </div>
    );
}
